import { getChartDefaultOptions } from "./ChartJsConfig";
import { useTheme } from "@mui/material/styles";
import appDefaultTheme from "@/config/muiTheme";
import { getChartTextColor } from "./chartTextColor";
import React, { useState, useEffect, useMemo, useContext } from "react";
import * as ReactChartJs from "react-chartjs-2";
import moment from "moment";
import { Typography } from "@mui/material";
import { RPNumberFormat } from "@/helpers/utils";
import { getDatasetDefaultOptions } from "./ChartJsConfig";
import DrillControl from "./DrillControl";
import { WIDGETS_WITH_DRILL_FEATURE, DRILL_LEVELS } from "@/config/constants";
import {
  formatAndSortData,
  isBrandHasDrillFeature,
} from "@/features/campaigns/shared/helpers/drillUpDownHelper";
import { GlobalContext } from "@/helpers/contexts/contexts";

type ChartRow = Record<string, string | number | null | undefined>;

type DataFormat = {
  prefix?: string;
  suffix: string;
};

type DataFormats = Record<string, DataFormat>;

type RangeDate = {
  year: string | number;
  month: string | number;
  date: string | number;
};

type ReportRange = {
  from: RangeDate;
  to: RangeDate;
};

type DrillValues = {
  filter?: string;
  customRange?: string[];
  group_id?: string | number;
  location_id?: string | number;
  channel_id?: string | number;
};

type TotalSpendChartProps = {
  data?: ChartRow[];
  labels: string[];
  option?: Record<string, unknown>;
  scales?: Record<string, unknown>;
  title?: string;
  formats?: DataFormats | null;
  showTotal?: boolean;
  widgetId?: number;
  values?: DrillValues;
  initialRange?: ReportRange | null;
};

type TooltipItem = {
  index: number;
};

type TooltipData = {
  datasets: Array<{
    label: string;
    data: Array<{ y?: string | number | null } | undefined>;
  }>;
};

const { Bar } = ReactChartJs as unknown as {
  Bar: React.ComponentType<Record<string, unknown>>;
};
const TotalSpendChart = ({
  data = [],
  labels,
  option = {},
  scales = {},
  title = "",
  formats = null,
  showTotal = false,
  widgetId,
  values,
  initialRange,
}: TotalSpendChartProps) => {
  const { selectedBrand } = useContext(GlobalContext);

  const providedTheme = useTheme();
  const theme = providedTheme.palette.neutral ? providedTheme : appDefaultTheme;
  const [chartData, setChartData] = useState<Record<string, unknown>>({
    labels: [],
    datasets: [],
  });
  const [drillLevel, setDrillLevel] = useState(DRILL_LEVELS.MONTH);

  const options = useMemo(
    () => ({
      ...getChartDefaultOptions(theme),
      tooltips: {
        ...getChartDefaultOptions(theme).tooltips,
        //intersect: false,
        callbacks: {
          title: (tooltipItem: Array<{ label: string }>) => {
            const label = tooltipItem[0].label;
            if (drillLevel === DRILL_LEVELS.DAY) {
              return moment(label, "YYYY-MM-DD").format("DD MMM YYYY");
            } else if (
              drillLevel === DRILL_LEVELS.QUARTER ||
              drillLevel === DRILL_LEVELS.YEAR
            ) {
              return label;
            } else {
              return moment(label, "YYYY-MM-DD").format("MMM YYYY");
            }
          },
          label: (tooltipItem: TooltipItem, data: TooltipData) => {
            const label = data.datasets[0].label;
            const val = data.datasets[0].data[tooltipItem.index] || {};
            const format = formats ? formats[labels[1]] : null;
            if (!val.y) return "";
            if (formats && format) {
              // Adding prefix and comma format
              return `${label}: ${RPNumberFormat({
                prefix: format.prefix || "$",

                suffix: format.suffix,
                thousand: ",",
              }).to(val.y)}`;
            }
            // Default to using $ if no format is provided
            return `${label}: ${RPNumberFormat({
              prefix: "$",
              suffix: "",
              thousand: ",",
            }).to(val.y)}`;
          },
        },
      },
      legend: {
        display: false,
        align: "end",
        labels: {
          boxWidth: 10,
          fontColor: getChartTextColor(theme),
          fontStyle: "bold",
          padding: 5,
          marginBottom: 1,
        },
      },
      plugins: {
        datalabels: {
          display: false,
        },
      },
      scales: {
        fontFamily: theme.typography.fontFamily,
        xAxes: [
          {
            gridLines: {
              display: false,
            },
            ticks: {
              fontColor: getChartTextColor(theme),
              callback: (value: string) => {
                if (drillLevel === DRILL_LEVELS.QUARTER) {
                  return value; // Display as "Q1-2025"
                } else if (drillLevel === DRILL_LEVELS.YEAR) {
                  return value; // Display as "2025"
                } else if (drillLevel === DRILL_LEVELS.DAY) {
                  return moment(value, "YYYY-MM-DD").format("DD"); // Display as "01 JAN"
                } else {
                  return moment(value, "YYYY-MM-DD").format("MMM");
                }
              },
            },
          },
        ],
        yAxes: [
          {
            id: "y-axis-0",
            position: "left",
            ticks: {
              fontColor: getChartTextColor(theme),
              min: 0,
              maxTicksLimit: 5,
              callback: (value: string | number) => {
                const format = formats ? formats[labels[1]] : null;
                if (!value) return "";
                if (formats && format) {
                  return RPNumberFormat({
                    prefix: format.prefix,
                    suffix: format.suffix,
                    thousand: ",",
                  }).to(value);
                }
                return RPNumberFormat().to(value);
              },
            },
          },
        ],
        ...scales,
      },
      ...option,
    }),
    [option, scales, drillLevel, theme]
  );

  const getDataSet = (
    data: ChartRow[] = [],
    currentDrillLevel = drillLevel
  ) => {
    const formatData = formatAndSortData(data, labels, currentDrillLevel);

    const datasets = [
      {
        label: title,
        data: formatData,
        yAxisID: "y-axis-0",
        ...getDatasetDefaultOptions(theme),
      },
    ];

    return {
      labels: formatData.map((d) => d.x),
      datasets: datasets,
    };
  };

  useEffect(() => {
    const initData = {
      labels: getDataSet()[0]?.data
        ? getDataSet()[0]?.data?.map((d) => d.x)
        : [],
      datasets: getDataSet(),
    };
    setChartData(initData);
  }, [data]);

  useEffect(() => {
    setChartData(getDataSet(data));
  }, [data]);

  useEffect(() => {
    // A color refresh must preserve the report data and drill level on screen.
    setChartData((previous) => ({
      ...previous,
      datasets: Array.isArray(previous.datasets)
        ? previous.datasets.map((dataset) => ({
            ...dataset,
            ...getDatasetDefaultOptions(theme),
          }))
        : [],
    }));
  }, [theme]);

  const getTotalSpendSum = () => {
    if (
      (data && !data) ||
      (data.length as unknown as { length?: number }).length === 0
    )
      return "$0";
    return RPNumberFormat({
      prefix: formats && formats[labels[1]] ? formats[labels[1]].prefix : "",
      suffix: formats && formats[labels[1]] ? formats[labels[1]].suffix : "",
      decimals: 2,
      thousand: ",",
    }).to(
      data.reduce(
        (t, d) =>
          d[labels[1]] && parseFloat(`${d[labels[1]]}`)
            ? t + parseFloat(`${d[labels[1]]}`)
            : t,
        0
      )
    );
  };

  const handleDrillChange = (newDrillLevel: string, newData: unknown[]) => {
    setDrillLevel(newDrillLevel);
    setChartData(() => getDataSet(newData as ChartRow[], newDrillLevel));
  };

  return (
    <div className="bar-chart total-spend-bar-chart">
      {showTotal && data && data.length ? (
        <div className="row text16P_Bold548E8D text-right mb-1">
          <div className="col-6">
            <Typography
              color="text.primary"
              variant="body2"
              className="fw-b text-left"
            >
              {`Total: ${getTotalSpendSum()}`}
            </Typography>
          </div>
          <div className="col-6">
            {isBrandHasDrillFeature(
              selectedBrand?.system_generated_brand_id as string | undefined
            ) &&
              widgetId &&
              WIDGETS_WITH_DRILL_FEATURE.includes(widgetId) && (
                <DrillControl
                  drillLevel={drillLevel}
                  setDrillLevel={setDrillLevel}
                  onDrillChange={handleDrillChange}
                  widgetId={widgetId}
                  values={values || {}}
                  initialRange={initialRange}
                />
              )}
          </div>
        </div>
      ) : null}
      <Bar data={chartData} options={options} />
    </div>
  );
};

export default TotalSpendChart;
